import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { tenantApi, propertyApi } from '../../services/api';
import { format } from 'date-fns';

const TenantForm = () => {
  const { id, propertyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  
  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      propertyId: propertyId ? parseInt(propertyId) : '',
      basicRent: '',
      charges: '',
      currency: 'USD',
      language: 'en',
      leaseStart: format(new Date(), 'yyyy-MM-dd'),
      leaseEnd: ''
    }
  });
  
  // Watch values for calculations
  const basicRent = watch('basicRent', 0);
  const charges = watch('charges', 0);
  const totalRent = parseFloat(basicRent || 0) + parseFloat(charges || 0);
  
  // Determine if we're editing an existing tenant
  const isEditing = Boolean(id);
  
  // Fetch properties for dropdown
  useEffect(() => {
    setLoading(true);
    propertyApi.getAll()
      .then(response => {
        setProperties(response.data);
      })
      .catch(error => {
        toast.error('Failed to load properties');
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  
  // Fetch tenant data if editing
  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      tenantApi.getById(id)
        .then(response => {
          const tenant = response.data;
          // Format dates for the form
          if (tenant.leaseStart) {
            tenant.leaseStart = format(new Date(tenant.leaseStart), 'yyyy-MM-dd');
          }
          if (tenant.leaseEnd) {
            tenant.leaseEnd = format(new Date(tenant.leaseEnd), 'yyyy-MM-dd');
          }
          reset(tenant);
        })
        .catch(error => {
          toast.error('Failed to load tenant');
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditing, reset]);
  
  const onSubmit = async (data) => {
    // Format dates for API
    const formattedData = {
      ...data,
      basicRent: parseFloat(data.basicRent),
      charges: parseFloat(data.charges),
      leaseStart: new Date(data.leaseStart).toISOString(),
      leaseEnd: data.leaseEnd ? new Date(data.leaseEnd).toISOString() : null
    };

    formattedData["propertyId"] = parseInt(data.propertyId);
    
    setLoading(true);
    try {
      if (isEditing) {
        await tenantApi.update(id, formattedData);
        toast.success('Tenant updated successfully');
      } else {
        await tenantApi.create(formattedData);
        toast.success('Tenant created successfully');
      }
      
      // Navigate back to the appropriate page
      if (propertyId) {
        navigate(`/properties/${propertyId}/tenants`);
      } else {
        navigate('/tenants');
      }
    } catch (error) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} tenant`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="card max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Tenant' : 'Add New Tenant'}
      </h1>
      
      {loading && isEditing ? (
        <div className="text-center py-4">Loading tenant data...</div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  className="form-input"
                  {...register('firstName', { required: 'First name is required' })}
                />
                {errors.firstName && <p className="form-error">{errors.firstName.message}</p>}
              </div>
              
              <div>
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  className="form-input"
                  {...register('lastName', { required: 'Last name is required' })}
                />
                {errors.lastName && <p className="form-error">{errors.lastName.message}</p>}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  {...register('email', { 
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && <p className="form-error">{errors.email.message}</p>}
              </div>
              
              <div>
                <label htmlFor="phone" className="form-label">Phone</label>
                <input
                  id="phone"
                  type="text"
                  className="form-input"
                  {...register('phone')}
                />
              </div>
            </div>
            
            {/* Property and Rent Information */}
            <div>
              <label htmlFor="propertyId" className="form-label">Property</label>
              <select
                id="propertyId"
                className="form-input"
                {...register('propertyId', { required: 'Property is required' })}
                disabled={Boolean(propertyId)}
              >
                <option value="">Select a property</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>
                    {property.name} - {property.address}, {property.city}
                  </option>
                ))}
              </select>
              {errors.propertyId && <p className="form-error">{errors.propertyId.message}</p>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="basicRent" className="form-label">Basic Rent</label>
                <input
                  id="basicRent"
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  {...register('basicRent', {
                    required: 'Basic rent is required',
                    min: { value: 0, message: 'Rent cannot be negative' }
                  })}
                />
                {errors.basicRent && <p className="form-error">{errors.basicRent.message}</p>}
              </div>

              <div>
                <label htmlFor="charges" className="form-label">Additional Charges</label>
                <input
                  id="charges"
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  {...register('charges', {
                    required: 'Charges are required',
                    min: { value: 0, message: 'Charges cannot be negative' }
                  })}
                />
                {errors.charges && <p className="form-error">{errors.charges.message}</p>}
              </div>

              <div>
                <label className="form-label">Total Rent</label>
                <div className="form-input bg-gray-50">{totalRent.toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="currency" className="form-label">Currency</label>
                <select
                  id="currency"
                  className="form-input"
                  {...register('currency')}
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="CHF">CHF - Swiss Franc</option>
                  <option value="CNY">CNY - Chinese Yuan</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="MXN">MXN - Mexican Peso</option>
                </select>
                {errors.currency && <p className="form-error">{errors.currency.message}</p>}
              </div>

              <div>
                <label htmlFor="language" className="form-label">Language</label>
                <select
                  id="language"
                  className="form-input"
                  {...register('language')}
                >
                  <option value="en">English</option>
                  <option value="fr">Français (French)</option>
                  <option value="es">Español (Spanish)</option>
                  <option value="de">Deutsch (German)</option>
                  <option value="it">Italiano (Italian)</option>
                  <option value="pt">Português (Portuguese)</option>
                  <option value="nl">Nederlands (Dutch)</option>
                  <option value="pl">Polski (Polish)</option>
                  <option value="ru">Русский (Russian)</option>
                  <option value="zh">中文 (Chinese)</option>
                  <option value="ja">日本語 (Japanese)</option>
                </select>
                {errors.language && <p className="form-error">{errors.language.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="leaseStart" className="form-label">Lease Start Date</label>
                <input
                  id="leaseStart"
                  type="date"
                  className="form-input"
                  {...register('leaseStart', { required: 'Lease start date is required' })}
                />
                {errors.leaseStart && <p className="form-error">{errors.leaseStart.message}</p>}
              </div>
              
              <div>
                <label htmlFor="leaseEnd" className="form-label">Lease End Date (Optional)</label>
                <input
                  id="leaseEnd"
                  type="date"
                  className="form-input"
                  {...register('leaseEnd')}
                />
              </div>
            </div>
            
            {isEditing && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Email Settings</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Customize the email template used when sending receipts to this tenant.
                </p>
                <Link
                  to={`/tenants/${id}/email-template`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Manage Email Template
                </Link>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => propertyId ? navigate(`/properties/${propertyId}/tenants`) : navigate('/tenants')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditing ? 'Update Tenant' : 'Add Tenant'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default TenantForm;